/**
 * AllDataNode — terminal DAG node showing a scrollable mini-table of runs.
 *
 * One per project. Clicking the header opens the full data table.
 * Clicking a row calls selectArtifact for cross-panel highlighting.
 * Purely visual — no backend entity, injected by the frontend transform pipeline.
 */

import { memo, useRef, useEffect, useCallback } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import {
  ALL_DATA_NODE_WIDTH,
  ALL_DATA_NODE_HEADER_HEIGHT,
  ALL_DATA_TABLE_ROW_HEIGHT,
  ALL_DATA_TABLE_MAX_VISIBLE,
  getAllDataNodeHeight,
} from "./dagNodeSizing";
import { CrispNodeContent } from "./CrispNodeContent";

interface ColumnDef {
  field: string;
  headerName: string;
}

export interface AllDataNodeData {
  label: string;
  runCount: number;
  countLabel?: string;
  onClick: () => void;
  columns?: ColumnDef[];
  rows?: Record<string, unknown>[];
  selectedNodeId?: string | null;
  onRowClick?: (nodeId: string) => void;
  [key: string]: unknown;
}

const HIDDEN_FIELDS = new Set(["_group", "_path_prefix", "_node_id", "_geometry_node_id"]);
const COL_MIN_WIDTH = 90;

function pickVisibleColumns(columns: ColumnDef[]): ColumnDef[] {
  return columns.filter((c) => !HIDDEN_FIELDS.has(c.field) && !c.field.startsWith("_"));
}

function formatCell(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "number") {
    return Math.abs(value) < 0.001 || Math.abs(value) >= 1e6
      ? value.toExponential(2)
      : Number.isInteger(value)
        ? String(value)
        : value.toFixed(4);
  }
  const s = String(value);
  return s.length > 16 ? s.slice(0, 15) + "…" : s;
}

function AllDataNodeInner({ data }: NodeProps) {
  const {
    label,
    runCount,
    countLabel = "runs",
    onClick,
    columns = [],
    rows = [],
    selectedNodeId,
    onRowClick,
  } = data as unknown as AllDataNodeData;

  const visibleCols = pickVisibleColumns(columns);
  const hasTable = visibleCols.length > 0 && rows.length > 0;
  const visibleRows = Math.min(rows.length, ALL_DATA_TABLE_MAX_VISIBLE);
  const tableBodyHeight = visibleRows * ALL_DATA_TABLE_ROW_HEIGHT;

  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to selected row
  useEffect(() => {
    if (!selectedNodeId || !scrollRef.current) return;
    const idx = rows.findIndex((r) => r._node_id === selectedNodeId);
    if (idx < 0) return;
    const top = idx * ALL_DATA_TABLE_ROW_HEIGHT;
    scrollRef.current.scrollTo({ top: top - tableBodyHeight / 2 + ALL_DATA_TABLE_ROW_HEIGHT / 2, behavior: "smooth" });
  }, [selectedNodeId, rows, tableBodyHeight]);

  const handleRowClick = useCallback(
    (e: React.MouseEvent, nodeId: string | undefined) => {
      e.stopPropagation();
      if (nodeId && onRowClick) onRowClick(nodeId);
    },
    [onRowClick],
  );

  const nodeHeight = hasTable
    ? getAllDataNodeHeight(rows.length)
    : ALL_DATA_NODE_HEADER_HEIGHT;

  return (
    <>
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <CrispNodeContent
        width={ALL_DATA_NODE_WIDTH}
        height={nodeHeight}
        style={{ boxSizing: "border-box", fontFamily: "var(--fc-font-family)", fontSize: 11 }}
      >
      {/* Header bar */}
      <div
        onClick={onClick}
        style={{
          height: ALL_DATA_NODE_HEADER_HEIGHT,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "0 10px",
          cursor: "pointer",
          boxSizing: "border-box",
        }}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, opacity: 0.8 }}>
          <rect x="1" y="1" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.4" />
          <line x1="1" y1="5.5" x2="15" y2="5.5" stroke="currentColor" strokeWidth="1.2" />
          <line x1="1" y1="10" x2="15" y2="10" stroke="currentColor" strokeWidth="1.2" />
          <line x1="6" y1="1" x2="6" y2="15" stroke="currentColor" strokeWidth="1.2" />
        </svg>

        <span style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3 }}>{label}</span>

        <span
          style={{
            fontSize: 9,
            fontWeight: 600,
            background: "rgba(255,255,255,0.2)",
            borderRadius: 8,
            padding: "1px 6px",
            whiteSpace: "nowrap",
            marginLeft: "auto",
          }}
        >
          {runCount} {countLabel}
        </span>
      </div>

      {/* Mini-table */}
      {hasTable && (
        <div
          className="nowheel"
          style={{
            borderTop: "1px solid rgba(255,255,255,0.15)",
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          <div style={{ minWidth: visibleCols.length * COL_MIN_WIDTH }}>
            {/* Column headers */}
            <div
              style={{
                display: "flex",
                height: ALL_DATA_TABLE_ROW_HEIGHT,
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                background: "rgba(255,255,255,0.05)",
              }}
            >
              {visibleCols.map((col) => (
                <div
                  key={col.field}
                  style={{
                    flex: 1,
                    minWidth: COL_MIN_WIDTH,
                    padding: "0 4px",
                    fontSize: 10,
                    fontWeight: 600,
                    opacity: 0.6,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    lineHeight: `${ALL_DATA_TABLE_ROW_HEIGHT}px`,
                  }}
                  title={col.headerName}
                >
                  {col.headerName}
                </div>
              ))}
            </div>

            {/* Scrollable rows */}
            <div
              ref={scrollRef}
              style={{
                maxHeight: tableBodyHeight,
                overflowY: rows.length > ALL_DATA_TABLE_MAX_VISIBLE ? "auto" : "hidden",
              }}
            >
              {rows.map((row, i) => {
                const nodeId = row._node_id as string | undefined;
                const isSelected = nodeId != null && nodeId === selectedNodeId;
                return (
                  <div
                    key={nodeId ?? i}
                    onClick={(e) => handleRowClick(e, nodeId)}
                    style={{
                      display: "flex",
                      height: ALL_DATA_TABLE_ROW_HEIGHT,
                      cursor: nodeId ? "pointer" : "default",
                      background: isSelected
                        ? "rgba(251,191,36,0.25)"
                        : i % 2 === 0
                          ? "transparent"
                          : "rgba(255,255,255,0.03)",
                      borderLeft: isSelected ? "2px solid #fbbf24" : "2px solid transparent",
                      transition: "background 0.15s",
                    }}
                  >
                    {visibleCols.map((col) => (
                      <div
                        key={col.field}
                        style={{
                          flex: 1,
                          minWidth: COL_MIN_WIDTH,
                          padding: "0 4px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          lineHeight: `${ALL_DATA_TABLE_ROW_HEIGHT}px`,
                          fontSize: 10,
                          fontVariantNumeric: "tabular-nums",
                        }}
                        title={String(row[col.field] ?? "")}
                      >
                        {formatCell(row[col.field])}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      </CrispNodeContent>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </>
  );
}

export const AllDataNode = memo(AllDataNodeInner);
export default AllDataNode;
